import React, {useContext, useState, useEffect, useRef} from "react";
import {Form, Select} from "antd";
import {ContentContext} from "./ContentContext";
import axios from "./axios";
import {BrandContext} from "./BrandContext";
import MathJaxHTMLContent from "./MathJaxHTMLContent";

export const SELECT_LEVELS = {
    SYLLABUS_GROUP: 'syllabusGroup',
    SYLLABUS: 'syllabus',
    TOPIC: 'topic',
    CONCEPT: 'concept',
    NONE: 'none',
}

const syllabusTopicConceptModel = {
    syllabusGroupId: null,
    syllabusId: null,
    topicId: null,
    conceptId: null,
    activeObject: null,
}

const SyllabusTopicConceptSelect = props => {
    const {
        form,
        selectLevel = SELECT_LEVELS.CONCEPT,
        defaultLevel = SELECT_LEVELS.CONCEPT,
        initialIds,
        resourceType,
        questionClusterTypes,
        autoMark,
        questionType,
        mediaType,
        customTopicsConceptsUrl,
        disabled = false,
        onLoadComplete,
        onChange,
    } = props;
    const brandConfig = useContext(BrandContext);
    const {content} = useContext(ContentContext);
    const [loading, setLoading] = useState(true);
    const [idsState, setIdsState] = useState(null);
    const [topicsConceptList, setTopicsConceptList] = useState([]);
    const initializedRef = useRef(false);
    const syllabusGroupRef = useRef(null),
        syllabusRef = useRef(null),
        lastKeyPressed = useRef(null),
        lastRef = useRef(null);

    useEffect(() => {
        if (!content || initializedRef.current) return;
        initializedRef.current = true;

        let newSyllabusGroupId = initialIds?.syllabusGroupId ?? content.currentSyllabusGroup.syllabusGroupId;

        if (initialIds?.syllabusId) {
            const syllabusGroup = content.syllabusGroupList.find(syllabusGroup =>
                syllabusGroup.syllabuses.some(syllabus => syllabus.syllabusId === initialIds?.syllabusId)
            );
            if (syllabusGroup) newSyllabusGroupId = syllabusGroup.syllabusGroupId;
        }

        updateSelects({...initialIds, syllabusGroupId: newSyllabusGroupId, isInitialization: true});
    }, [content, initialIds]);

    const updateSelects = async ({syllabusGroupId: updateSyllabusGroupId, syllabusId: updateSyllabusId, topicId: updateTopicId, conceptId: updateConceptId, isInitialization = false}) => {
        const findNextAvailableId = ({items = [], idKey, selectId, selectName, userCurrentId, defaultLevels}) => {
            if (selectId != null) {
                return selectId;
            } else if (!defaultLevels.includes(defaultLevel)) {
                return 0;
            } else if (items.some(item => !item.disabled && item[idKey] === userCurrentId)) {
                return userCurrentId;
            } else {
                const firstAvailable = items.find(item => !item?.disabled);
                if (firstAvailable) {
                    return firstAvailable[idKey];
                } else {
                    return `No available ${selectName}`;
                }
            }
        };
        const validateField = ({selectId, fieldValueObj, defaultLevels}) => {
            let error = false;
            if (!defaultLevels.includes(defaultLevel)) {
                if (!fieldValueObj) error = true;
            }
            if (error || fieldValueObj?.disabled) {
                return [{
                    name: selectId,
                    errors: [" "],
                }];
            } else {
                return [{
                    name: selectId,
                    errors: [],
                }];
            }
        }
        const getActiveSelectObject = (currentIds, currentTopicsConceptList) => {
            if (selectLevel === SELECT_LEVELS.SYLLABUS_GROUP) {
                return content.syllabusGroupList.find(sg => sg.syllabusGroupId === currentIds.syllabusGroupId);
            } else if (selectLevel === SELECT_LEVELS.SYLLABUS) {
                return content.syllabusList.find(s => s.syllabusId === currentIds.syllabusId);
            } else if (selectLevel === SELECT_LEVELS.TOPIC) {
                return currentTopicsConceptList.find(t => t.topicId === currentIds.topicId);
            } else {
                return currentTopicsConceptList.find(t => t.topicId === currentIds.topicId)?.concepts.find(c => c.conceptId === currentIds.conceptId);
            }
        }

        const newIdsState = {...(idsState || syllabusTopicConceptModel)};
        let newTopicsConceptList = topicsConceptList;

        try {
            if (
                updateSyllabusGroupId === idsState?.syllabusGroupId &&
                updateSyllabusId === idsState?.syllabusId &&
                updateTopicId === idsState?.topicId &&
                updateConceptId === idsState?.conceptId
            ) return;

            if (updateSyllabusGroupId != null) {
                newIdsState.syllabusGroupId = updateSyllabusGroupId;
            }

            if (updateSyllabusGroupId != null || updateSyllabusId != null) {
                const syllabusesList = content.syllabusList
                    .filter(syllabus => syllabus.syllabusGroupId === newIdsState.syllabusGroupId);
                newIdsState.syllabusId = findNextAvailableId({
                    items: syllabusesList,
                    idKey: 'syllabusId',
                    selectId: updateSyllabusId,
                    selectName: brandConfig.syllabusesName,
                    userCurrentId: content?.currentSyllabus?.syllabusId,
                    defaultLevels: [SELECT_LEVELS.SYLLABUS, SELECT_LEVELS.TOPIC, SELECT_LEVELS.CONCEPT]
                });
            }

            if (updateSyllabusGroupId != null || updateSyllabusId != null || updateTopicId != null) {
                setLoading(true);
                let url = "/web/ui/nav/syllabus/" + newIdsState.syllabusId + "/topics";
                let params = {};
                if (customTopicsConceptsUrl) {
                    url = customTopicsConceptsUrl;
                    params.syllabusId = newIdsState.syllabusId;
                } else {
                    const queryParams = { resourceType, questionClusterTypes, autoMark, questionType, mediaType };
                    Object.keys(queryParams).forEach(key => {
                        if (queryParams[key] != null) params[key] = queryParams[key];
                    });
                }
                const response = await axios.get(url, {params});
                newTopicsConceptList = response.data;
                setTopicsConceptList(newTopicsConceptList);
                newIdsState.topicId = findNextAvailableId({
                    items: newTopicsConceptList,
                    idKey: 'topicId',
                    selectId: updateTopicId,
                    selectName: brandConfig.topicsName,
                    userCurrentId: content?.currentTopic?.topicId,
                    defaultLevels: [SELECT_LEVELS.TOPIC, SELECT_LEVELS.CONCEPT]
                });
            }

            if (updateSyllabusGroupId != null || updateSyllabusId != null || updateTopicId != null || updateConceptId != null) {
                const topic = newTopicsConceptList.find(t => t.topicId === newIdsState.topicId);
                newIdsState.conceptId = findNextAvailableId({
                    items: topic?.concepts,
                    idKey: 'conceptId',
                    selectId: updateConceptId,
                    selectName: brandConfig.conceptsName,
                    userCurrentId: content?.currentConcept?.conceptId,
                    defaultLevels: [SELECT_LEVELS.CONCEPT]
                });
            }

            if (form) {
                form.setFieldsValue({...form.getFieldsValue, ...newIdsState});
                form.setFields(validateField({
                    selectId: "syllabusGroupId",
                    fieldValueObj: content.syllabusGroupList.find(sg => sg.syllabusGroupId === newIdsState.syllabusGroupId),
                    defaultLevels: []
                }));
                form.setFields(validateField({
                    selectId: "syllabusId",
                    fieldValueObj: content.syllabusList.find(s => s.syllabusId === newIdsState.syllabusId),
                    defaultLevels: [SELECT_LEVELS.SYLLABUS_GROUP]
                }));
                form.setFields(validateField({
                    selectId: "topicId",
                    fieldValueObj: newTopicsConceptList.find(t => t.topicId === newIdsState.topicId),
                    defaultLevels: [SELECT_LEVELS.SYLLABUS_GROUP, SELECT_LEVELS.SYLLABUS]
                }));
                form.setFields(validateField({
                    selectId: "conceptId",
                    fieldValueObj: newTopicsConceptList.find(t => t.topicId === newIdsState.topicId)?.concepts.find(c => c.conceptId === newIdsState.conceptId),
                    defaultLevels: [SELECT_LEVELS.SYLLABUS_GROUP, SELECT_LEVELS.SYLLABUS, SELECT_LEVELS.TOPIC]
                }));
            }

            newIdsState.activeObject = getActiveSelectObject(newIdsState, newTopicsConceptList);
            setIdsState(newIdsState);

            if (isInitialization && onLoadComplete && typeof onLoadComplete === "function") {
                onLoadComplete(newIdsState.activeObject);
            } else if (!isInitialization && onChange && typeof onChange === "function") {
                onChange(newIdsState.activeObject);
            }
        } catch (error) {
            console.error("Failed to update selects", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(()=>{
        if (!loading && lastKeyPressed.current === 'Enter') {
            lastRef.current?.focus();
        }
    },[loading]);

    return (
        <>
            <Form.Item
                label={brandConfig.syllabusGroupName}
                name="syllabusGroupId"
            >
                <Select
                    ref={syllabusGroupRef}
                    // className={loading ? 'ch-select-loading' : ''}
                    onKeyDownCapture={(e) => {
                        lastKeyPressed.current = e.code;
                    }}
                    onClick={()=>{lastKeyPressed.current = null;}}
                    onSelect={()=>{lastRef.current = syllabusGroupRef.current;}}
                    onChange={syllabusGroupId => updateSelects({syllabusGroupId: syllabusGroupId})}
                    virtual={false}
                    showSearch={false}
                    loading={loading}
                    disabled={disabled || loading || (form.getFieldError("syllabusGroupId")?.length > 0)}
                >
                    {defaultLevel === SELECT_LEVELS.NONE &&
                        <Select.Option value={0}>All</Select.Option>
                    }
                    {content.syllabusGroupList.map(syllabusGroup => (
                        <Select.Option key={syllabusGroup.syllabusGroupId} value={syllabusGroup.syllabusGroupId}>{syllabusGroup.syllabusGroupName}</Select.Option>
                    ))}
                </Select>
            </Form.Item>
            {(selectLevel === SELECT_LEVELS.SYLLABUS || selectLevel === SELECT_LEVELS.TOPIC || selectLevel === SELECT_LEVELS.CONCEPT) && (
                <Form.Item
                    label={brandConfig.syllabusName}
                    name="syllabusId"
                >
                    <Select
                        ref={syllabusRef}
                        // className={loading ? 'ch-select-loading' : ''}
                        onKeyDownCapture={(e) => {
                            lastKeyPressed.current = e.code;
                        }}
                        onClick={()=>{lastKeyPressed.current = null;}}
                        onSelect={()=>{lastRef.current = syllabusRef.current;}}
                        onChange={syllabusId => updateSelects({syllabusId: syllabusId})}
                        virtual={false}
                        showSearch={false}
                        loading={loading}
                        disabled={disabled || loading || (form.getFieldError("syllabusId")?.length > 0)}
                    >
                        {defaultLevel === SELECT_LEVELS.SYLLABUS_GROUP &&
                            <Select.Option value={0}>All</Select.Option>
                        }
                        {content.syllabusList
                            .filter(syllabus => syllabus.syllabusGroupId === idsState?.syllabusGroupId)
                            .map(syllabus => (
                                <Select.Option key={syllabus.syllabusId} value={syllabus.syllabusId}>{syllabus.syllabusName}</Select.Option>
                            ))}
                    </Select>
                </Form.Item>
            )}
            {(selectLevel === SELECT_LEVELS.TOPIC || selectLevel === SELECT_LEVELS.CONCEPT) && (
                <Form.Item
                    label={brandConfig.topicName}
                    name="topicId"
                >
                    <Select
                        // className={loading ? 'ch-select-loading' : ''}
                        onChange={topicId => updateSelects({topicId: topicId})}
                        virtual={false}
                        showSearch={false}
                        loading={loading}
                        disabled={disabled || loading || (form.getFieldError("topicId")?.length > 0)}
                    >
                        {(defaultLevel === SELECT_LEVELS.SYLLABUS || defaultLevel === SELECT_LEVELS.SYLLABUS_GROUP) &&
                            <Select.Option value={0}>All</Select.Option>
                        }
                        {topicsConceptList.map(topic => (
                            <Select.Option
                                key={topic.topicId}
                                value={topic.topicId}
                                disabled={topic.disabled}
                            >
                                {topic.topicName}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
            )}
            {selectLevel === SELECT_LEVELS.CONCEPT && (
                <Form.Item
                    label={brandConfig.conceptName}
                    name="conceptId"
                >
                    <Select
                        // className={loading ? 'ch-select-loading' : ''}
                        onChange={conceptId => updateSelects({conceptId: conceptId})}
                        virtual={false}
                        showSearch={false}
                        loading={loading}
                        disabled={disabled || loading || (form.getFieldError("conceptId")?.length > 0)}
                    >
                        {(defaultLevel === SELECT_LEVELS.TOPIC || defaultLevel === SELECT_LEVELS.SYLLABUS || defaultLevel === SELECT_LEVELS.SYLLABUS_GROUP) &&
                            <Select.Option value={0}>All</Select.Option>
                        }
                        {topicsConceptList != null &&
                            topicsConceptList
                                .find(topic => topic.topicId === idsState?.topicId)?.concepts
                                .map(concept => {
                                    return (
                                        <Select.Option
                                            key={concept.conceptId}
                                            value={concept.conceptId}
                                            disabled={concept.disabled}
                                        >
                                            <MathJaxHTMLContent html={concept.conceptName}/>
                                        </Select.Option>
                                    )
                                })}
                    </Select>
                </Form.Item>
            )}
        </>
    )
}

export default React.memo(SyllabusTopicConceptSelect);